import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSeats, useCustomerInfo, useExistingReservations, useSeatValidation, SeatValidationNotification } from "./";
import { SeatMap, ReservationSummary, CustomerForm, TheaterHeader } from "./";
import { SessionTimer, Toast, ReservationConfirmationModal } from "../../shared/components";
import { useAppSelector } from "../../shared/hooks";
import { theaterReservationService } from "../../shared/services";
import { formatSeatId } from "../../shared/utils/seatUtils";

// Arma la lista de asientos a mostrar en el modal de confirmación, marcando cuáles ya están pagados
// y cuáles son palcos (para aclarar que ocupan varias ubicaciones)
const buildSeatItems = (reservations, palcoCapacity) =>
  reservations.map(r => ({
    label: formatSeatId(r.compositeId),
    isPaid: r.status === 'sold',
    isPalco: r.compositeId.startsWith('BOX-'),
    palcoCapacity,
  }));

// Cuenta las entradas reales: un palco vale palcoCapacity entradas, no una fila = una entrada
const countTickets = (reservations, palcoCapacity) =>
  reservations.reduce((sum, r) => sum + (r.compositeId.startsWith('BOX-') ? (palcoCapacity || 4) : 1), 0);

const TheaterReservation = () => {
  // Memoize Redux selectors to prevent unnecessary re-renders
  const userData = useAppSelector(state => state.auth.userData);
  const eventData = useAppSelector(state => state.auth.eventData);
  const maxTicketsPerCode = eventData?.maxTicketsPerCode || 6;
  const openSale = eventData?.isOpenSale || false;
  
  // Estados para manejo de reservas
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showToast, setShowToast] = useState(false);
  const [isModifyingReservations, setIsModifyingReservations] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    mode: 'created',
    ticketCount: 0,
    paidCount: 0,
    deadlineDate: null,
    seatLabels: [],
  });
  
  // Estado para rastrear reservas que deben ser canceladas
  const [reservationsToCancel, setReservationsToCancel] = useState([]);

  // Evita repetir el alert de "ya tenés una reserva" en cada refresco de reservas
  const hasShownExistingReservationAlert = useRef(false);

  // Hook para validación de asientos
  const {
    validationError,
    recommendations,
    showValidationError,
    clearValidationError,
    showRecommendations,
    clearRecommendations,
    selectRecommendedSeat
  } = useSeatValidation();

  // Hook para manejar información
  const {
    customerInfo,
    updateCustomerInfo,
    isFormValid,
    resetForm,
  } = useCustomerInfo(userData);

  // Hook para manejar reservas existentes
  const {
    existingReservations,
    loading: loadingExistingReservations,
    error: existingReservationsError,
    loadExistingReservations,
    getExistingSeatIds,
    hasActiveReservations,
  } = useExistingReservations(userData?.id, eventData?.id, false); // false = contexto teatro

  const {
    plantaBajaSeats,
    primerPisoSeats,
    selectedSeats,
    setSelectedSeats,
    totalPrice,
    handleSeatClick,
    getSeatClassName,
    effectiveSeatCount,
    palcoCapacity,
    canSelectMore,
    allSeats,
    loadOccupiedSeats,
    clearSelectedSeats,
    setSeatDeselectedCallback,
    setSeatReselectedCallback,
    seatTypesLoading,
    seatTypesError,
    createDefaults,
    seatTypeList,
    seatTypePriceMap,
    validateSeatSelection,
    getRecommendedSeats,
  } = useSeats(maxTicketsPerCode, eventData?.id, reservationsToCancel, false, existingReservations, [], openSale, false, false);

  // Función para mostrar toast
  const showToastMessage = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    // Ocultar toast después de 5 segundos
    setTimeout(() => {
      setShowToast(false);
    }, 5000);
  }, []);

  // Función para mostrar el alert de confirmación de reserva
  const showReservationConfirmation = useCallback(({ mode, ticketCount, paidCount, deadlineDate, seatLabels }) => {
    setConfirmationModal({ isOpen: true, mode, ticketCount, paidCount, deadlineDate, seatLabels });
  }, []);

  const closeReservationConfirmation = useCallback(() => {
    setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Efecto para mostrar recomendaciones cuando hay asientos individuales vacíos
  useEffect(() => {
    if (selectedSeats.length > 1) {
      const recs = getRecommendedSeats(selectedSeats);
      if (recs.length > 0) {
        showRecommendations(recs);
      } else {
        clearRecommendations();
      }
    } else {
      clearRecommendations();
    }
  }, [selectedSeats, getRecommendedSeats, showRecommendations, clearRecommendations]);

  // Configurar callback para mostrar errores de validación
  useEffect(() => {
    // Configurar función global para mostrar errores de validación
    window.showSeatValidationError = showValidationError;
    
    return () => {
      // Limpiar función global al desmontar
      delete window.showSeatValidationError;
    };
  }, [showValidationError]);

  // Función para manejar la selección de asiento recomendado
  const handleSelectRecommendedSeat = useCallback((seatId) => {
    selectRecommendedSeat(seatId, handleSeatClick);
  }, [selectRecommendedSeat, handleSeatClick]);

  // Función para manejar la deselección de asientos existentes (SIN cancelación inmediata)
  const handleSeatDeselected = useCallback((seatId) => {
    if (isModifyingReservations) {
      // Buscar la reserva correspondiente al asiento deseleccionado
      const reservation = existingReservations.find(r => r.compositeId === seatId);
      if (reservation) {
        // Agregar a la lista de reservas a cancelar (sin cancelar inmediatamente)
        setReservationsToCancel(prev => {
          const exists = prev.find(r => r.id === reservation.id);
          if (!exists) {
            return [...prev, reservation];
          }
          return prev;
        });
      }
    }
  }, [isModifyingReservations, existingReservations]);

  // Función para manejar la reselección de asientos marcados para cancelación
  const handleSeatReselected = useCallback((seatId) => {
    if (isModifyingReservations) {
      // Buscar la reserva correspondiente al asiento reseleccionado
      const reservation = existingReservations.find(r => r.compositeId === seatId);
      if (reservation) {
        // Remover de la lista de reservas a cancelar
        setReservationsToCancel(prev => prev.filter(r => r.id !== reservation.id));
      }
    }
  }, [isModifyingReservations, existingReservations]);

  // Configurar el callback de deselección de asientos
  React.useEffect(() => {
    setSeatDeselectedCallback(handleSeatDeselected, existingReservations);
  }, [setSeatDeselectedCallback, handleSeatDeselected, existingReservations]);

  // Configurar el callback de reselección de asientos
  React.useEffect(() => {
    setSeatReselectedCallback(handleSeatReselected);
  }, [setSeatReselectedCallback, handleSeatReselected]);

  // Cargar reservas existentes cuando se carguen
  React.useEffect(() => {
    if (!loadingExistingReservations && existingReservations.length > 0 && hasActiveReservations()) {
      const existingSeatIds = getExistingSeatIds();
      setSelectedSeats(existingSeatIds);
      setIsModifyingReservations(true);
      setReservationsToCancel([]); // Limpiar reservas a cancelar al cargar nuevas reservas

      // Avisar una sola vez, al ingresar, que ya tiene entradas reservadas (no retiradas)
      if (!hasShownExistingReservationAlert.current) {
        hasShownExistingReservationAlert.current = true;
        const pendingReservations = existingReservations.filter(r => r.status === 'pending');
        const paidCount = countTickets(existingReservations.filter(r => r.status === 'sold'), palcoCapacity);
        showReservationConfirmation({
          mode: 'existing',
          ticketCount: countTickets(pendingReservations, palcoCapacity),
          paidCount,
          deadlineDate: pendingReservations.find(r => r.accessCode?.expiresAt)?.accessCode?.expiresAt || null,
          seatLabels: buildSeatItems(existingReservations, palcoCapacity),
        });
      }
    }
  }, [loadingExistingReservations, existingReservations, hasActiveReservations, getExistingSeatIds, setSelectedSeats, showReservationConfirmation, palcoCapacity]);

  const handleCompleteReservation = async () => {
    if (!eventData?.id || !userData?.accessCode?.id || selectedSeats.length === 0) {
      if((selectedSeats.length === 0 && !isModifyingReservations) || (isModifyingReservations && reservationsToCancel.length === 0)){
        showToastMessage('Error: Faltan datos para completar la reserva', 'error');
        return;
      }
    }

    // Validación final de asientos antes de crear la reserva
    if (selectedSeats.length > 1) {
      const validation = validateSeatSelection(selectedSeats);
      if (!validation.isValid) {
        showToastMessage(validation.message, 'error');
        return;
      }
    }

    setIsCreatingReservation(true);

    try {
      if (isModifyingReservations) {
        // Lógica para modificar reservas existentes
        await handleModifyExistingReservations();
      } else {
        // Lógica para crear nuevas reservas
        await handleCreateNewReservations();
      }
    } catch (error) {
      console.error('Error al procesar la reserva:', error);
      showToastMessage(
        error.message || 'Error al procesar la reserva. Inténtalo de nuevo.',
        'error'
      );
    } finally {
      setIsCreatingReservation(false);
    }
  };

  // Función para crear nuevas reservas
  const handleCreateNewReservations = async () => {
    // Preparar los datos de la reserva
    const seatsData = selectedSeats.map(seatId => {
      return {
        compositeId: seatId,
      };
    });

    // Usar las reservas acumuladas que deben ser canceladas
    const reservationsToCancelIds = reservationsToCancel.map(reservation => reservation.id);

    const reservationData = {
      eventId: eventData.id,
      studentId: userData.id,
      accessCodeId: userData.accessCode.id,
      seats: seatsData,
      status: 'pending',
      notes: `Reserva creada por ${customerInfo.name}`,
      existingReservationsToCancel: reservationsToCancelIds,
      customerInfo: {
        name: customerInfo.name,
        phone: customerInfo.phone,
      },
    };

    // Crear la reserva en el backend
    await theaterReservationService.createReservation(reservationData);

    // Traer el estado real y actualizado de las reservas (incluye fecha de vencimiento del código)
    const freshReservations = await theaterReservationService.getReservationsByStudent(userData.id, eventData.id);
    const pendingReservations = freshReservations.filter(r => r.status === 'pending');
    const paidCount = countTickets(freshReservations.filter(r => r.status === 'sold'), palcoCapacity);
    const deadlineDate = freshReservations.find(r => r.accessCode?.expiresAt)?.accessCode?.expiresAt || null;
    const seatLabels = buildSeatItems(freshReservations, palcoCapacity);

    // Mostrar alert de confirmación
    showReservationConfirmation({
      mode: 'created',
      ticketCount: countTickets(pendingReservations, palcoCapacity),
      paidCount,
      deadlineDate,
      seatLabels,
    });

    // Recargar asientos ocupados y reservas existentes
    await loadOccupiedSeats();
    await loadExistingReservations();

    // Resetear el formulario, asientos seleccionados y reservas a cancelar
    resetForm();
    setReservationsToCancel([]); // Limpiar la lista de reservas a cancelar
    clearSelectedSeats();
  };

  // Función para modificar reservas existentes
  const handleModifyExistingReservations = async () => {
    const existingSeatIds = getExistingSeatIds();
    const newSeatIds = selectedSeats;
    
    // Validación final de asientos antes de modificar la reserva
    if (newSeatIds.length > 1) {
      const validation = validateSeatSelection(newSeatIds);
      if (!validation.isValid) {
        showToastMessage(validation.message, 'error');
        return;
      }
    }
    
    // Usar las reservas acumuladas que deben ser canceladas
    const reservationsToCancelIds = reservationsToCancel.map(reservation => reservation.id);
    
    // Crear nuevas reservas para asientos que no existían
    const seatsToCreate = newSeatIds.filter(seatId => !existingSeatIds.includes(seatId));
    
    // Procesar la operación incluso si solo hay cancelaciones
    if (seatsToCreate.length > 0 || reservationsToCancelIds.length > 0) {
      const seatsData = seatsToCreate.map(seatId => ({
        compositeId: seatId,
      }));

      const reservationData = {
        eventId: eventData.id,
        studentId: userData.id,
        accessCodeId: userData.accessCode.id,
        seats: seatsData,
        status: 'pending',
        notes: `Reserva modificada por ${customerInfo.name}`,
        existingReservationsToCancel: reservationsToCancelIds, // Usar las reservas acumuladas
        customerInfo: {
          name: customerInfo.name,
          phone: customerInfo.phone,
        },
      };

      await theaterReservationService.createReservation(reservationData);
    }

    // Traer el estado real y actualizado de las reservas (incluye fecha de vencimiento del código)
    const freshReservations = await theaterReservationService.getReservationsByStudent(userData.id, eventData.id);
    const pendingReservations = freshReservations.filter(r => r.status === 'pending');
    const paidCount = countTickets(freshReservations.filter(r => r.status === 'sold'), palcoCapacity);
    const deadlineDate = freshReservations.find(r => r.accessCode?.expiresAt)?.accessCode?.expiresAt || null;
    const seatLabels = buildSeatItems(freshReservations, palcoCapacity);

    // Mostrar alert de confirmación apropiado
    showReservationConfirmation({
      mode: reservationsToCancelIds.length > 0 && seatsToCreate.length === 0 ? 'cancelled' : 'modified',
      ticketCount: countTickets(pendingReservations, palcoCapacity),
      paidCount,
      deadlineDate,
      seatLabels,
    });

    // Recargar asientos ocupados y reservas existentes
    await loadOccupiedSeats();
    await loadExistingReservations();

    // Resetear el estado de modificación y las reservas a cancelar
    setIsModifyingReservations(false);
    setReservationsToCancel([]); // Limpiar la lista de reservas a cancelar
    clearSelectedSeats();
  };



  // Función para cancelar la modificación de reservas
  const handleCancelModification = () => {
    setIsModifyingReservations(false);
    setReservationsToCancel([]); // Limpiar las reservas a cancelar
    clearSelectedSeats();
    showToastMessage(
      'Modificación cancelada. Tus reservas originales se mantienen.',
      'info'
    );
  };

  
  if (seatTypesLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <SessionTimer />
        <TheaterHeader userData={userData} eventData={eventData} />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando tipos de asientos...</p>
          </div>
        </div>
      </div>
    );
  }

  
  if (seatTypesError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <SessionTimer />
        <TheaterHeader userData={userData} eventData={eventData} />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">Error al cargar tipos de asientos</p>
            <button
              onClick={createDefaults}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Crear Tipos por Defecto
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Timer de sesión */}
      <SessionTimer />
      
      {/* Header del teatro */}
      <TheaterHeader 
        userData={userData}
        eventData={eventData}
      />
      
      {/* Mostrar reservas existentes solo si están cargando */}
      {loadingExistingReservations && (
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-700">Cargando tus reservas existentes...</span>
            </div>
          </div>
        </div>
      )}

      {/* Mostrar error si hay problema al cargar reservas */}
      {existingReservationsError && (
        <div className="mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-600 text-sm">
              <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Error: {existingReservationsError}
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Mapa de asientos */}
        <div className="lg:col-span-3">
          {/* Indicador de reservas existentes cargadas */}
          {isModifyingReservations && hasActiveReservations() && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center text-blue-700">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">
                  Tus reservas existentes están seleccionadas. Puedes modificar tu selección.
                </span>
              </div>
            </div>
          )}

          {/* Indicador de reservas marcadas para cancelación */}
          {isModifyingReservations && reservationsToCancel.length > 0 && (
            <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center text-orange-700">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm font-medium">
                    Modificando {reservationsToCancel.length} reserva{reservationsToCancel.length > 1 ? 's' : ''}.
                    Presiona "Actualizar Reserva" para confirmar los cambios.
                </span>
              </div>
            </div>
          )}


          
          <SeatMap
            allSeats={allSeats}
            plantaBajaSeats={plantaBajaSeats}
            primerPisoSeats={primerPisoSeats}
            handleSeatClick={handleSeatClick}
            getSeatClassName={getSeatClassName}
            selectedSeats={selectedSeats}
            reservationsToCancel={reservationsToCancel}
            maxTicketsPerCode={maxTicketsPerCode}
            seatTypeList={seatTypeList}
            seatTypePriceMap={seatTypePriceMap}
          />
        </div>

        {/* Panel lateral derecho */}
        <div className="lg:col-span-1 space-y-6">
          {/* Resumen de reserva */}
          <ReservationSummary
            selectedSeats={selectedSeats}
            totalPrice={totalPrice}
            maxTicketsPerCode={maxTicketsPerCode}
            canSelectMore={canSelectMore}
            allSeats={allSeats}
            effectiveSeatCount={effectiveSeatCount}
            palcoCapacity={palcoCapacity}
            isModifyingReservations={isModifyingReservations}
            existingReservations={existingReservations}
            reservationsToCancel={reservationsToCancel}
            reservationsToReturn={[]}
            isOpenSale={openSale}
            onCancelModification={handleCancelModification}
          />

          {/* Formulario del cliente */}
          {(selectedSeats.length > 0 || isModifyingReservations) && (
            <CustomerForm
              customerInfo={customerInfo}
              updateCustomerInfo={updateCustomerInfo}
              isFormValid={isFormValid}
              onCompleteReservation={handleCompleteReservation}
              isCreatingReservation={isCreatingReservation}
              isModifyingReservations={isModifyingReservations}
            />
          )}
        </div>
      </div>
      
      {/* Toast para mostrar mensajes */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />

      {/* Alert de confirmación de reserva */}
      <ReservationConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeReservationConfirmation}
        mode={confirmationModal.mode}
        ticketCount={confirmationModal.ticketCount}
        paidCount={confirmationModal.paidCount}
        deadlineDate={confirmationModal.deadlineDate}
        seatLabels={confirmationModal.seatLabels}
      />

      {/* Notificaciones de validación de asientos */}
      <SeatValidationNotification
        validationError={validationError}
        recommendations={recommendations}
        onClearError={clearValidationError}
        onClearRecommendations={clearRecommendations}
        onSelectRecommendedSeat={handleSelectRecommendedSeat}
      />
    </div>
  );
};

export default TheaterReservation;